import { AlertBar } from '#GameFrontend/components/ui/AlertBar/AlertBar';
import { Button } from '#GameFrontend/components/ui/button';
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	type CarouselApi,
} from '#GameFrontend/components/ui/carousel';
import { Input } from '#GameFrontend/components/ui/input';
import { useLoading } from '#GameFrontend/contexts/LoadingContext';
import { socket } from '#GameFrontend/services/socket';
import ColorSelect from '#GameFrontend/unauthorized/components/register/ColorSelect';
import { createGeneByBloodline, generateFilenameByGene } from '#GameFrontend/unauthorized/lib/GeneSelecter';
import { RuleSet, validate } from '#GameFrontend/unauthorized/lib/Validation';
import { Gene, Starter } from '#GameFrontend/unauthorized/types/Starter';
import { defaultStarterFormGene, StarterFormProps } from '#GameFrontend/unauthorized/types/StarterForm';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Step2Props {
	bloodline: Starter;
	nextStep: () => void;
	formData: StarterFormProps;
	setFormData: (data: StarterFormProps) => void;
}

const Step2: React.FC<Step2Props> = ({ bloodline, formData, setFormData, nextStep }) => {
	const { setIsLoading } = useLoading();
	const [isAlertVisible, setIsAlertVisible] = useState(true);
	const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [totalItems, setTotalItems] = useState(4);
	const [isNewImageLoading, setIsNewImageLoading] = useState(false);
	const path = `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORT !== '80' && process.env.REACT_APP_PORT !== '443' ? `:${process.env.REACT_APP_PORT}` : ''}/image/horses/starter/`;
	const [imagePath, setImagePath] = useState<string>(`${path}${bloodline.id}/preview.png`);
	const [backupImagePath, setBackupImagePath] = useState<string>(`${path}${bloodline.id}/preview.png`);
	const horseNameRuleSet = useRef<RuleSet | undefined>(undefined);
	const [error, setError] = useState<string>('');
	const { t } = useTranslation();
	const initGeneData = () => {
		const newGenes = createGeneByBloodline(defaultStarterFormGene(), bloodline);
		setFormData({
			...formData,
			genes: { ...newGenes },
		});
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({
			...formData,
			horseName: e.target.value,
		});
	};

	const handleMaleChange = () => {
		setFormData({
			...formData,
			horseIsMale: true,
		});
	};

	const handleFemaleChange = () => {
		setFormData({
			...formData,
			horseIsMale: false,
		});
	};

	const handleBaseChange = (name: string) => {
		const newGenes = { ...formData.genes };
		if (name !== 'grey') {
			newGenes.base = name;
		} else {
			newGenes.grey = !newGenes.grey;
		}
		setFormData({
			...formData,
			genes: { ...newGenes },
		});
	};

	const handleCreamAndDunChange = (name: string, value: boolean) => {
		const [base, type] = name.split('_');
		const newGenes = { ...formData.genes };
		newGenes.base = base;
		if (type === 'dun') {
			newGenes.dun = value;
			if (newGenes.cream && !bloodline.starterGenes[base as keyof Gene].dun.cream.value) {
				newGenes.cream = false;
			}
		} else {
			newGenes.cream = value;
			if (newGenes.dun && !bloodline.starterGenes[base as keyof Gene].dun.cream.value) {
				newGenes.dun = false;
			}
		}

		setFormData({
			...formData,
			genes: { ...newGenes },
		});
	};

	const handleMarkChange = (name: string, value: boolean) => {
		const fieldName = name.split('-')[0];
		setFormData({
			...formData,
			genes: {
				...formData.genes,
				marks: {
					...formData.genes.marks,
					[fieldName]: value,
				},
			},
		});
	};

	const handlePatternChange = (name: string, value: boolean) => {
		setFormData({
			...formData,
			genes: {
				...formData.genes,
				tobiano: name === 'tobiano' ? value : false,
				overo: name === 'overo' ? value : false,
				leopard: name === 'leopard' ? value : false,
			},
		});
	};

	const handleImageLoad = () => {
		setIsLoading(false);
		setIsNewImageLoading(false);
	};

	const handleImageError = () => {
		setIsLoading(false);
		setIsNewImageLoading(false);
	};

	const handleNext = () => {
		const result = validate('horseName', horseNameRuleSet.current, formData.horseName);
		if (result.valid) {
			setIsLoading(true);
			socket.emit('Horse:Name:Validation', { name: formData.horseName });
		} else {
			setIsAlertVisible(true);
			setError(result.message);
		}
	};

	useEffect(() => {
		if (!carouselApi) return;

		const updateCarouselState = () => {
			const newIndex = carouselApi.selectedScrollSnap();
			setCurrentIndex(newIndex);
			setTotalItems(carouselApi.scrollSnapList().length);
		};

		updateCarouselState();

		carouselApi.on('select', updateCarouselState);

		return () => {
			carouselApi.off('select', updateCarouselState); // Clean up on unmount
		};
	}, [carouselApi]);

	useEffect(() => {
		setIsLoading(true);
		socket.timeout(5000).emit('Horse:Name:ValidationConfig', {});
		initGeneData();
	}, [bloodline]);

	useEffect(() => {
		const filename = generateFilenameByGene(formData.genes, formData.horseIsMale);
		const newImagePath = `${path}${bloodline.id}/${filename}.png`;

		if (newImagePath !== imagePath) {
			setIsNewImageLoading(true);
			setIsLoading(true);
			setBackupImagePath(imagePath); // Set the current image path as the backup
			setImagePath(newImagePath);
		}
	}, [formData.genes, formData.horseIsMale]);

	useEffect(() => {}, [error]);

	useEffect(() => {
		socket.on('Horse:Name:ValidationConfig', (data) => {
			setIsLoading(false);
			if (data.success) {
				horseNameRuleSet.current = data.data as RuleSet;
			} else {
				setError('Error Loading Configuration, please refresh!');
			}
		});
		socket.on('Horse:Name:Validation', (data) => {
			setIsLoading(false);
			if (data.success) {
				nextStep();
			} else {
				setIsAlertVisible(true);
				if (data.translation.length > 0) {
					const translation = data.translation.map(
						(translation: { key: string; params: Record<string, string> }) =>
							t(translation.key, translation.params)
					);
					setError(translation.join('\n'));
				} else {
					setError(data.message);
				}
			}
		});
	}, []);

	return (
		<>
			{error && (
				<AlertBar type="error" message={error} isVisible={isAlertVisible} setIsVisible={setIsAlertVisible} />
			)}
			<div className={`lg:py-6 lg:pl-6 xl:pl-0 flex flex-col flex-grow text-white text-center`}>
				<h2 className="text-4xl text-white mb-0 lg:mb-4 text-center drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] relative z-30">
					<div className="flex items-center justify-center">
						<svg
							width="140"
							height="60"
							xmlns="http://www.w3.org/2000/svg"
							className="lg:hidden text-center inline-block "
						>
							<text x="10" y="40" className="outlined-text svg-text-md font-lobster" textAnchor="right">
								{t('name')}
							</text>
						</svg>
						<svg
							width="120"
							height="60"
							xmlns="http://www.w3.org/2000/svg"
							className="hidden lg:inline-block text-center"
						>
							<text x="50%" y="40" className="outlined-text svg-text-md font-lobster" textAnchor="middle">
								{t('name')}
							</text>
						</svg>
						<Input
							className="mx-2 bg-[#0911317F] bg-opacity-25 max-w-80 lg:text-xl"
							name="horseName"
							id="horseName"
							value={formData.horseName}
							placeholder={t('horseName.placeholder')}
							onChange={handleNameChange}
						/>
						<div
							className={`sprite-46 male ${formData.horseIsMale ? 'active' : ''} cursor-pointer min-w-[42px]`}
							onClick={handleMaleChange}
						></div>
						<div
							className={`sprite-46 female ${!formData.horseIsMale ? 'active' : ''} cursor-pointer min-w-[42px]`}
							onClick={handleFemaleChange}
						></div>
					</div>
				</h2>
				<div className="max-w-full xxl:max-w-[80%] lg:absolute xl:relative xxl:relative lg:ml-[-70px] xl:ml-0 xxl:ml-0 z-10">
					<img
						key={imagePath}
						src={imagePath}
						alt="Horse"
						className={`object-contain w-full h-full ${isNewImageLoading ? 'hidden' : 'block'}`}
						onLoad={handleImageLoad}
						onError={handleImageError}
					/>
					<img
						key={`${backupImagePath}-backup`}
						src={backupImagePath}
						alt="Horse"
						className={`object-contain w-full h-full ${isNewImageLoading ? 'block' : 'hidden'}`}
					/>
				</div>
				<div className="flex flex-col flex-grow h-[156px] lg:hidden items-center mt-[-64px]">
					<div className="flex-grow grid grid-cols-[49%_17%_17%_1fr] items-center pt-4">
						<Carousel setApi={setCarouselApi} opts={{ loop: true }} orientation="vertical">
							<CarouselContent className="h-[88px]">
								{bloodline.starterGenes.black.value && (
									<CarouselItem key="black">
										<div className="flex flex-row">
											<ColorSelect
												className="black"
												isActive={formData.genes.base === 'black'}
												isLocked={false}
												category="base"
												onChange={handleBaseChange}
											/>
											{bloodline.starterGenes.black.dun.value && (
												<ColorSelect
													className="black_dun"
													isActive={formData.genes.base === 'black' && formData.genes.dun}
													isLocked={bloodline.mustHaveGenes.black.dun.value}
													category="dun"
													onChange={handleCreamAndDunChange}
												/>
											)}
											{(bloodline.starterGenes.black.cream.value ||
												bloodline.starterGenes.black.dun.cream.value) && (
												<ColorSelect
													className="black_cream"
													isActive={formData.genes.base === 'black' && formData.genes.cream}
													isLocked={
														bloodline.mustHaveGenes.black.cream.value ||
														bloodline.mustHaveGenes.black.dun.cream.value
													}
													category="cream"
													onChange={handleCreamAndDunChange}
												/>
											)}
										</div>
									</CarouselItem>
								)}
								{bloodline.starterGenes.bay.value && (
									<CarouselItem key="bay">
										<div className="flex flex-row">
											<ColorSelect
												className="bay"
												isActive={formData.genes.base === 'bay'}
												isLocked={false}
												category="base"
												onChange={handleBaseChange}
											/>
											{bloodline.starterGenes.bay.dun.value && (
												<ColorSelect
													className="bay_dun"
													isActive={formData.genes.base === 'bay' && formData.genes.dun}
													isLocked={bloodline.mustHaveGenes.bay.dun.value}
													category="dun"
													onChange={handleCreamAndDunChange}
												/>
											)}
											{(bloodline.starterGenes.bay.cream.value ||
												bloodline.starterGenes.bay.dun.cream.value) && (
												<ColorSelect
													className="bay_cream"
													isActive={formData.genes.base === 'bay' && formData.genes.cream}
													isLocked={
														bloodline.mustHaveGenes.bay.cream.value ||
														bloodline.mustHaveGenes.bay.dun.cream.value
													}
													category="cream"
													onChange={handleCreamAndDunChange}
												/>
											)}
										</div>
									</CarouselItem>
								)}
								{bloodline.starterGenes.chestnut.value && (
									<CarouselItem key="chestnut">
										<div className="flex flex-row">
											<ColorSelect
												className="chestnut"
												isActive={formData.genes.base === 'chestnut'}
												isLocked={false}
												category="base"
												onChange={handleBaseChange}
											/>
											{bloodline.starterGenes.chestnut.dun.value && (
												<ColorSelect
													className="chestnut_dun"
													isActive={formData.genes.base === 'chestnut' && formData.genes.dun}
													isLocked={bloodline.mustHaveGenes.chestnut.dun.value}
													category="dun"
													onChange={handleCreamAndDunChange}
												/>
											)}
											{(bloodline.starterGenes.chestnut.cream.value ||
												bloodline.starterGenes.chestnut.dun.cream.value) && (
												<ColorSelect
													className="chestnut_cream"
													isActive={
														formData.genes.base === 'chestnut' && formData.genes.cream
													}
													isLocked={
														bloodline.mustHaveGenes.chestnut.cream.value ||
														bloodline.mustHaveGenes.chestnut.dun.cream.value
													}
													category="cream"
													onChange={handleCreamAndDunChange}
												/>
											)}
										</div>
									</CarouselItem>
								)}
							</CarouselContent>
							<CarouselPrevious variant="link" className="mt-8 text-brown" />
							<CarouselNext variant="link" className="mb-6 text-brown" />
						</Carousel>
						<Carousel orientation="vertical" setApi={setCarouselApi} opts={{ loop: true }}>
							<CarouselContent className="h-[88px]">
								{bloodline.starterGenes.tobiano && (
									<CarouselItem>
										<ColorSelect
											className="tobiano"
											isActive={formData.genes.tobiano}
											isLocked={bloodline.mustHaveGenes.tobiano}
											category="pattern"
											onChange={handlePatternChange}
										/>
									</CarouselItem>
								)}
								{bloodline.starterGenes.overo && (
									<CarouselItem>
										<ColorSelect
											className="overo"
											isActive={formData.genes.overo}
											isLocked={bloodline.mustHaveGenes.overo}
											category="pattern"
											onChange={handlePatternChange}
										/>
									</CarouselItem>
								)}
								{bloodline.starterGenes.leopard && (
									<CarouselItem>
										<ColorSelect
											className="leopard"
											isActive={formData.genes.leopard}
											isLocked={bloodline.mustHaveGenes.leopard}
											category="pattern"
											onChange={handlePatternChange}
										/>
									</CarouselItem>
								)}
								{bloodline.starterGenes.grey && (
									<CarouselItem key="grey">
										<ColorSelect
											className="grey"
											isActive={formData.genes.grey}
											isLocked={bloodline.mustHaveGenes.grey}
											category="grey"
											onChange={handleBaseChange}
											isRadio={false}
										/>
									</CarouselItem>
								)}
							</CarouselContent>
							<CarouselPrevious variant="link" className="mt-8 text-brown" />
							<CarouselNext variant="link" className="mb-6 text-brown" />
						</Carousel>
						<Carousel orientation="vertical" setApi={setCarouselApi} opts={{ loop: true }}>
							<CarouselContent className="h-[88px]">
								{bloodline.starterGenes.marks.head && (
									<CarouselItem>
										<ColorSelect
											className="head-mark"
											isActive={formData.genes.marks.head}
											isLocked={bloodline.mustHaveGenes.marks.head}
											category="marks-head"
											isRadio={false}
											onChange={handleMarkChange}
										/>
									</CarouselItem>
								)}
								{bloodline.starterGenes.marks.leg && (
									<CarouselItem>
										<ColorSelect
											className="leg-mark"
											isActive={formData.genes.marks.leg}
											isLocked={bloodline.mustHaveGenes.marks.leg}
											category="marks-leg"
											isRadio={false}
											onChange={handleMarkChange}
										/>
									</CarouselItem>
								)}
							</CarouselContent>
							<CarouselPrevious variant="link" className="mt-8 text-brown" />
							<CarouselNext variant="link" className="mb-6 text-brown" />
						</Carousel>
						<div className="flex flex-grow justify-end relative pt-2 px-2">
							<Button
								variant="ghost"
								className="text-white hover:text-[#fb923c] hover:bg-white bg-[#fb923c] lg:hidden relative z-30 px-2"
								onClick={handleNext}
							>
								{t('next.button')}
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div className={`pl-12 py-6 lg:pt-2 xl:p-6  flex-col flex-grow text-white hidden lg:flex`}>
				<div className="flex flex-grow flex-col">
					<h2 className="text-3xl font-normal xl:mb-2 font-lobster text-[#fb923c] drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)]">
						<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg">
							<text x="10" y="50" className="outlined-text svg-text-sm">
								{t('horse.customize.colors')}
							</text>
						</svg>
					</h2>
					<div className="flex flex-col">
						<div className="flex flex-row">
							{bloodline.starterGenes.black.value && (
								<ColorSelect
									className="black"
									isActive={formData.genes.base === 'black'}
									isLocked={false}
									category="base"
									onChange={handleBaseChange}
								/>
							)}
							{bloodline.starterGenes.bay.value && (
								<ColorSelect
									className="bay"
									isActive={formData.genes.base === 'bay'}
									isLocked={false}
									category="base"
									onChange={handleBaseChange}
								/>
							)}
							{bloodline.starterGenes.chestnut.value && (
								<ColorSelect
									className="chestnut"
									isActive={formData.genes.base === 'chestnut'}
									isLocked={false}
									category="base"
									onChange={handleBaseChange}
								/>
							)}
							{bloodline.starterGenes.grey && (
								<ColorSelect
									className="grey"
									isActive={formData.genes.grey}
									isLocked={bloodline.mustHaveGenes.grey}
									category="grey"
									onChange={handleBaseChange}
									isRadio={false}
								/>
							)}
						</div>
						<div className="flex flex-row">
							{bloodline.starterGenes.black.dun.value && (
								<ColorSelect
									className="black_dun"
									isActive={formData.genes.base === 'black' && formData.genes.dun}
									isLocked={bloodline.mustHaveGenes.black.dun.value}
									category="dun"
									onChange={handleCreamAndDunChange}
								/>
							)}
							{bloodline.starterGenes.bay.dun.value && (
								<ColorSelect
									className="bay_dun"
									isActive={formData.genes.base === 'bay' && formData.genes.dun}
									isLocked={bloodline.mustHaveGenes.bay.dun.value}
									category="dun"
									onChange={handleCreamAndDunChange}
								/>
							)}
							{bloodline.starterGenes.chestnut.dun.value && (
								<ColorSelect
									className="chestnut_dun"
									isActive={formData.genes.base === 'chestnut' && formData.genes.dun}
									isLocked={bloodline.mustHaveGenes.chestnut.dun.value}
									category="dun"
									onChange={handleCreamAndDunChange}
								/>
							)}
						</div>
						<div className="flex flex-row">
							{(bloodline.starterGenes.black.cream.value ||
								bloodline.starterGenes.black.dun.cream.value) && (
								<ColorSelect
									className="black_cream"
									isActive={formData.genes.base === 'black' && formData.genes.cream}
									isLocked={
										bloodline.mustHaveGenes.black.cream.value ||
										bloodline.mustHaveGenes.black.dun.cream.value
									}
									category="cream"
									onChange={handleCreamAndDunChange}
								/>
							)}
							{(bloodline.starterGenes.bay.cream.value || bloodline.starterGenes.bay.dun.cream.value) && (
								<ColorSelect
									className="bay_cream"
									isActive={formData.genes.base === 'bay' && formData.genes.cream}
									isLocked={
										bloodline.mustHaveGenes.bay.cream.value ||
										bloodline.mustHaveGenes.bay.dun.cream.value
									}
									category="cream"
									onChange={handleCreamAndDunChange}
								/>
							)}
							{(bloodline.starterGenes.chestnut.cream.value ||
								bloodline.starterGenes.chestnut.dun.cream.value) && (
								<ColorSelect
									className="chestnut_cream"
									isActive={formData.genes.base === 'chestnut' && formData.genes.cream}
									isLocked={
										bloodline.mustHaveGenes.chestnut.cream.value ||
										bloodline.mustHaveGenes.chestnut.dun.cream.value
									}
									category="cream"
									onChange={handleCreamAndDunChange}
								/>
							)}
						</div>
						<h2
							className={`text-3xl font-normal xl:mb-2 font-lobster text-[#fb923c] drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] ${!bloodline.starterGenes.tobiano && !bloodline.starterGenes.overo && !bloodline.starterGenes.leopard ? 'hidden' : ''}`}
						>
							<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg">
								<text x="10" y="50" className="outlined-text svg-text-sm">
									{t('horse.customize.patterns')}
								</text>
							</svg>
						</h2>
						<div className="flex flex-row">
							{bloodline.starterGenes.tobiano && (
								<ColorSelect
									className="tobiano"
									isActive={formData.genes.tobiano}
									isLocked={bloodline.mustHaveGenes.tobiano}
									category="pattern"
									onChange={handlePatternChange}
								/>
							)}
							{bloodline.starterGenes.overo && (
								<ColorSelect
									className="overo"
									isActive={formData.genes.overo}
									isLocked={bloodline.mustHaveGenes.overo}
									category="pattern"
									onChange={handlePatternChange}
								/>
							)}
							{bloodline.starterGenes.leopard && (
								<ColorSelect
									className="leopard"
									isActive={formData.genes.leopard}
									isLocked={bloodline.mustHaveGenes.leopard}
									category="pattern"
									onChange={handlePatternChange}
								/>
							)}
						</div>
						<h2
							className={`text-3xl font-normal xl:mb-2 font-lobster text-[#fb923c] drop-shadow-[0_4px_4px_rgba(0,0,0,0.4)] ${!bloodline.starterGenes.marks.head && !bloodline.starterGenes.marks.leg ? 'hidden' : ''}`}
						>
							<svg width="300" height="55" xmlns="http://www.w3.org/2000/svg">
								<text x="10" y="50" className="outlined-text svg-text-sm">
									{t('horse.customize.marks')}
								</text>
							</svg>
						</h2>
						<div className="flex flex-row">
							{bloodline.starterGenes.marks.head && (
								<ColorSelect
									className="head-mark"
									isActive={formData.genes.marks.head}
									isLocked={bloodline.mustHaveGenes.marks.head}
									category="marks-head"
									isRadio={false}
									onChange={handleMarkChange}
								/>
							)}
							{bloodline.starterGenes.marks.leg && (
								<ColorSelect
									className="leg-mark"
									isActive={formData.genes.marks.leg}
									isLocked={bloodline.mustHaveGenes.marks.leg}
									category="marks-leg"
									isRadio={false}
									onChange={handleMarkChange}
								/>
							)}
						</div>
					</div>
				</div>
				<div className="flex justify-end mt-8">
					<Button
						variant="ghost"
						className="text-white hover:text-[#fb923c] hover:bg-white bg-[#fb923c] hidden lg:inline-block"
						onClick={handleNext}
					>
						{t('next.button') + ' >'}
					</Button>
				</div>
			</div>
		</>
	);
};
export default Step2;
